import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import mixpanel, { type Dict } from "mixpanel-browser";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { sendCloudwatchMetric } from "services/external/cloudwatch";
import type { AnalyticsContextProps } from "types/mainTypes";
import { MixpanelTabletTypeEnum } from "types/mixpanelTypes";
import { setAnalyticsInstance } from "utils/analytics/analyticsUtils";
import { getFridgeId } from "utils/browser/localStorage";
import { log } from "utils/logger";
import { v4 as uuid, v5 as uuidv5 } from "uuid";
import { shouldEnableFeature } from "../utils/shouldEnableFeature";

/**
 * Represents the context for analytics.
 */
export const AnalyticsContext = createContext<AnalyticsContextProps>({
	track: () => null,
	reset: () => null,
	identify: () => null,
	people: {
		set: () => null,
	},
});

const CLOUDWATCH_UPTIME_CHECK_INTERVAL = 30_000;
// Fixed namespace for UUID v5 generation of Mixpanel $insert_id (RFC 4122 app-specific namespace)
const MIXPANEL_DEDUP_NAMESPACE = "a11e4d6c-b8f0-4a3e-9c7d-2f5b8e1a3d09";

const MIXPANEL_INIT_OPTIONS = {
	api_host: "https://api-eu.mixpanel.com",
	property_blacklist: [
		"$city",
		"$region",
		"mp_country_code",
		"$browser",
		"$browser_version",
		"$device",
		"$current_url",
		"$initial_referrer",
		"$initial_referring_domain",
		"$os",
		"mp_lib",
		"$referrer",
		"$referring_domain",
		"$screen_height",
		"$screen_width",
		"$search_engine",
		"mp_keyword",
	],
};

/**
 * AnalyticsContextProvider component
 */
export const AnalyticsContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const mixpanelInstance = mixpanel;
	// Construct the Customer.io browser instance only once. The `new AnalyticsBrowser()`
	// argument to `useRef` would otherwise run on every render.
	const customerIoInstanceRef = useRef<AnalyticsBrowser | null>(null);
	if (customerIoInstanceRef.current === null) {
		try {
			customerIoInstanceRef.current = new AnalyticsBrowser();
		} catch (e) {
			// Keep the instance null and degrade gracefully rather than throwing during render
			// (which would escape past the error boundary mounted below this provider).
			log("Error while constructing Customer.io browser", "error", e);
		}
	}
	const customerIoInstance = customerIoInstanceRef.current;
	const fridgeId = getFridgeId();
	const initializedRef = useRef(false);
	const sessionIdRef = useRef<string>(uuid());
	const AppVersion = import.meta.env.APP_VERSION;
	const AppBuild = import.meta.env.VITE_BUILD_NUMBER;

	const track = useCallback(
		(name: string, props: Dict) => {
			try {
				const useAnalytics = shouldEnableFeature("useAnalytics");

				// Generate deterministic insert_id (valid UUID) for event deduplication
				// Uses UUID v5 to create a deterministic UUID from event name + identifier,
				// ensuring different event types for the same entity get distinct IDs
				const eventIdentifier = props.$order_id || props.$user_id || props.$dish_type_id || "";
				const dedupKey = eventIdentifier ? `${name}:${eventIdentifier}` : `${name}:${sessionIdRef.current}`;
				const insertId = uuidv5(dedupKey, MIXPANEL_DEDUP_NAMESPACE);

				if (useAnalytics) {
					mixpanelInstance.track(name, {
						...props,
						$insert_id: insertId,
					});

					customerIoInstance
						?.track(name, {
							...props,
							$source_of_stats: "Tablet",
							$tabletType: MixpanelTabletTypeEnum.Master,
							$fridge_id: fridgeId,
							$tablet_version: AppVersion,
							$tablet_build: AppBuild,
						})
						?.catch(() => {
							log("Error while sending the track event to customer.io", "error");
						});
				} else {
					log("Not master tablet, not tracking", "info", false, name, {
						...props,
						insertId: insertId,
					});
				}
			} catch (e) {
				// Analytics is best-effort: never let a tracking failure surface to the caller.
				log("Error while tracking event", "error", e);
			}
		},
		[fridgeId, customerIoInstance?.track],
	);

	const reset = useCallback(() => {
		try {
			// Generate new session ID on reset
			const newSessionId = uuid();
			sessionIdRef.current = newSessionId;

			const commonProperties = {
				$session_id: newSessionId,
				$source_of_stats: "Tablet",
				$tabletType: MixpanelTabletTypeEnum.Master,
				$fridge_id: fridgeId,
				$tablet_version: AppVersion,
				$tablet_build: AppBuild,
			};

			// Reset Mixpanel
			mixpanelInstance.reset();
			mixpanelInstance.register(commonProperties);

			// Reset Customer.io
			if (Config.tools.customerIoWriteKey) {
				customerIoInstance?.reset();
			}
		} catch (e) {
			log("Error while resetting analytics", "error", e);
		}
	}, [fridgeId, customerIoInstance?.reset]);

	const identify = useCallback(
		(userId: string, userData?: { [key: string]: string | number | null }) => {
			try {
				// Identify user in Mixpanel
				mixpanelInstance.identify(userId);

				// Identify user in Customer.io with anonymous ID (sessionId)
				// This allows CustomerIO to associate previous anonymous events
				// from this session with the identified user
				const traits = {
					...userData,
					// Keep fridgeId as device_id for device tracking
					device_id: fridgeId,
				};
				customerIoInstance?.identify(userId, traits);
			} catch (e) {
				log("Error while identifying user", "error", e);
			}
		},
		[fridgeId, customerIoInstance?.identify],
	);

	const people = useMemo(
		() => ({
			set: (data: { [key: string]: string | number }) => {
				try {
					// Set user properties in Mixpanel
					mixpanelInstance.people.set(data);
				} catch (e) {
					log("Error while setting people properties", "error", e);
				}
			},
		}),
		[],
	);

	useEffect(() => {
		if (initializedRef.current) {
			return; // guard clause
		}
		initializedRef.current = true;

		const commonProperties = {
			$session_id: sessionIdRef.current,
			$source_of_stats: "Tablet",
			$tabletType: MixpanelTabletTypeEnum.Master,
			$fridge_id: fridgeId,
			$tablet_version: AppVersion,
			$tablet_build: AppBuild,
		};

		// Initialize Mixpanel. Kept inside the effect (not the render body) so a synchronous
		// failure can never escape into render and crash the app past the error boundary.
		try {
			mixpanelInstance.init(Config.tools.mixpanelProjectId, MIXPANEL_INIT_OPTIONS);
			mixpanelInstance.register(commonProperties);
		} catch (e) {
			log("Error while initializing Mixpanel", "error", e);
		}

		// Initialize Customer.io
		if (Config.tools.customerIoWriteKey) {
			try {
				customerIoInstance
					?.load({
						cdnURL: "https://cdp-eu.customer.io",
						writeKey: Config.tools.customerIoWriteKey,
					})
					?.catch((e) => {
						log("Error while initializing Customer.io", "error", e);
					});

				customerIoInstance?.identify(undefined, {
					device_id: fridgeId,
				});
			} catch (e) {
				log("Error while initializing Customer.io", "error", e);
			}
		}

		setAnalyticsInstance({
			track: track,
			reset: reset,
			identify: identify,
			people: people,
		});
	}, [track, reset, identify, people, fridgeId, customerIoInstance?.identify, customerIoInstance?.load]);

	useEffect(() => {
		const shouldTrackToCloudwatch = shouldEnableFeature("useCloudwatch");
		const interval = setInterval(() => {
			sendCloudwatchMetric(shouldTrackToCloudwatch);
		}, CLOUDWATCH_UPTIME_CHECK_INTERVAL);

		return () => {
			clearInterval(interval);
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			track: track,
			reset: reset,
			identify: identify,
			people: people,
		}),
		[track, reset, identify, people],
	);

	return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

/**
 * Retrieves the analytics context from the React Context API.
 *
 * @return {Object} The analytics context.
 */
export const useAnalyticsContext = () => useContext(AnalyticsContext);
